import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Tag,
  MapPin,
  Building2,
  PlayCircle,
  Sparkles,
} from "lucide-react";

/**
 * Welcome card — appears on the Dashboard for first-time users until they've
 * run their first scan. A single inline card (no modal) that guides through
 * the 4 activation steps:
 *
 *   1. Pick your business category
 *   2. Set market scope (& city)
 *   3. Import your website
 *   4. Run your first scan
 *
 * Once step 4 is complete the card is hidden by the parent.
 */

function StepRow({ index, done, current, icon: Icon, title, desc, cta, to, testId }) {
  const showCta = !done && current;
  return (
    <li
      data-testid={testId}
      className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
        current ? "bg-lm-tint/60 border border-lm-primary/30" : "bg-lm-surface border border-lm-border"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
          done
            ? "bg-lm-primary text-white"
            : current
            ? "bg-white text-lm-primary border border-lm-primary/40"
            : "bg-lm-tint text-lm-text3"
        }`}
      >
        {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">
            Step {index}
          </div>
          {done && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-primary">
              Done
            </div>
          )}
        </div>
        <div className={`text-sm font-medium ${done ? "text-lm-text2 line-through" : "text-lm-text"}`}>
          {title}
        </div>
        <div className="text-xs text-lm-text3 mt-0.5">{desc}</div>
      </div>
      {showCta && (
        <Link
          to={to}
          data-testid={`${testId}-cta`}
          className="inline-flex items-center gap-1.5 self-center bg-lm-primary text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-lm-primaryHover shrink-0"
        >
          {cta}
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </li>
  );
}

export default function WelcomeCard({ workspace, brand, hasFirstScan, userName }) {
  const hasCategory = !!workspace?.business_category;
  const hasScope = !!workspace?.market_scope;
  const hasBrand = !!brand;
  const hasScan = !!hasFirstScan;

  // Advance current-step marker to the first unfinished step
  const currentStep = !hasCategory
    ? 1
    : !hasScope
    ? 2
    : !hasBrand
    ? 3
    : !hasScan
    ? 4
    : 0;

  // Only render when at least one step is incomplete
  if (hasCategory && hasScope && hasBrand && hasScan) return null;

  const first = (userName || "").split(" ")[0];

  return (
    <div
      data-testid="welcome-card"
      className="mb-6 rounded-xl border border-lm-primary/25 bg-gradient-to-br from-lm-tint/70 to-white p-5 md:p-6 shadow-[0_8px_24px_-12px_rgba(15,85,53,0.25)]"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-lm-primary text-white flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-mono uppercase tracking-widest text-lm-primary mb-0.5">
            Welcome to Leadmato
          </div>
          <h2 className="text-lg md:text-xl font-display font-semibold tracking-tight">
            {first ? `Let's get you a first win, ${first}.` : "Let's get you a first win."}
          </h2>
          <p className="text-sm text-lm-text2 mt-1 max-w-2xl">
            Four short steps. Every one you complete tightens the scan — no wasted first attempts.
          </p>
        </div>
      </div>

      <ol className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="welcome-card-steps">
        <StepRow
          index={1}
          done={hasCategory}
          current={currentStep === 1}
          icon={Tag}
          title="Pick your business category"
          desc="So we know which platforms & registers to use for your business."
          cta="Choose category"
          to="/onboarding"
          testId="welcome-step-category"
        />
        <StepRow
          index={2}
          done={hasScope}
          current={currentStep === 2}
          icon={MapPin}
          title="Set market scope & city"
          desc="Local city, All India, US or Global. City asks beat national queries by 100×."
          cta="Set scope"
          to="/onboarding"
          testId="welcome-step-scope"
        />
        <StepRow
          index={3}
          done={hasBrand}
          current={currentStep === 3}
          icon={Building2}
          title="Import your website"
          desc="We auto-draft your brand, ICPs and buyer keywords. Editable before you save."
          cta="Import website"
          to="/onboarding"
          testId="welcome-step-brand"
        />
        <StepRow
          index={4}
          done={hasScan}
          current={currentStep === 4}
          icon={PlayCircle}
          title="Run your first scan"
          desc="One click. If it returns nothing usable, we refund the scan — no muddy first impressions."
          cta="Run first scan"
          to="/app/find-customers"
          testId="welcome-step-first-scan"
        />
      </ol>
    </div>
  );
}
